"use client";

import {
	type EventTypes,
	type Handler,
	type UserGestureConfig,
	useGesture,
} from "@use-gesture/react";
import { type Dispatch, type SetStateAction, createContext, useContext, useState } from "react";
import { useCanvasStore } from "../../../hooks/useCanvasStore";

interface DragContext {
	isDragging: boolean;
	setIsDragging: Dispatch<SetStateAction<boolean>>;
}

export const DragContext = createContext<DragContext | null>(null);

export function useDragContext() {
	const context = useContext(DragContext);

	if (context === null) throw new Error("DragContext is not available");

	return context;
}

interface UseDragProps {
	onDrag: Handler<"drag", EventTypes["drag"]>;
	onDragStart?: Handler<"drag", EventTypes["drag"]>;
	onDragEnd?: Handler<"drag", EventTypes["drag"]>;
	config: UserGestureConfig;
}

export function useDrag(props: UseDragProps) {
	const context = useDragContext();
	const { showDimensions, setShowDimensions } = useCanvasStore();
	const [isDragging, setIsDragging] = useState(false);
	const [wasShowingDimensions, setWasShowingDimensions] = useState(false);

	function setDraggingState(value: boolean) {
		context?.setIsDragging(value);
		setIsDragging(value);
	}

	// wrapper around the default drag hook to save the dragging status in global
	// context so we can prevent the camera from moving for example.
	const bind = useGesture(
		{
			onDragStart: (state) => {
				setWasShowingDimensions(showDimensions);
				if (showDimensions) setShowDimensions(false);

				if (state.event instanceof TouchEvent && state.event.touches.length !== 1) {
					return;
				}

				setDraggingState(true);

				if (props.onDragStart) props.onDragStart(state);
			},
			onDrag: (state) => {
				setDraggingState(state.active);

				if (state.active) {
					props.onDrag(state);
				} else {
					if (props.onDragEnd) props.onDragEnd(state);
				}
			},
			onDragEnd: () => {
				if (wasShowingDimensions) setShowDimensions(true);
			},
		},
		props.config,
	);

	return { bind, isDragging };
}
